// Copyright (c) StrangeBee 2023

import { Radar, RadarConfig } from '@ant-design/charts';
import React, { ReactElement, useRef } from 'react';
import { useListAllFields } from '@Queries/describeQueries';
import { WidgetError } from '@Organisms/Dashboard/DashboardDetails/WidgetError';
import { WidgetOutput, ChartOutput, DescribeFieldAttribute, UserOutput } from 'thehive-sdk';
import { useListAllUsers } from '@Queries/userQueries';
import { WidgetLoading } from '@Organisms/Dashboard/DashboardDetails/WidgetLoading/WidgetLoading';
import { UseQueryResult } from 'react-query';

import './Chart.scss';

interface RadarChartOutput {
    name: string;
    value: number;
    [key: string]: any;
}

const prepareChartData = (
    radarData: ChartOutput[] | undefined,
    describeField?: DescribeFieldAttribute,
    userList?: UserOutput[],
): RadarChartOutput[] => {
    const fields =
        radarData?.reduce((acc, { _key, count }) => {
            const index = describeField?.values.findIndex((value) => `${value}` === `${_key}`);

            if (count === 0) {
                return acc;
            }

            const name =
                describeField?.type === 'user'
                    ? userList?.find(({ login }) => login === _key + '')?.name || _key + ''
                    : index !== undefined && index !== -1
                    ? describeField?.labels[index] || ''
                    : _key + '';

            return [
                ...acc,
                {
                    name,
                    value: count,
                },
            ];
        }, [] as RadarChartOutput[]) || [];

    return fields;
};

interface RadarWidgetProps {
    widget: WidgetOutput;
    query?: UseQueryResult<ChartOutput[]>;
}

export const RadarWidget = ({ widget, query }: RadarWidgetProps): ReactElement => {
    const chartRef = useRef();

    const { data: describeData } = useListAllFields();
    const { entity, field = '' } = widget.options || {};
    const { data: userList = [] } = useListAllUsers();

    if (query?.isLoading) {
        return (
            <div className="widget__empty">
                <WidgetLoading />
            </div>
        );
    }

    if (query?.status === 'error') {
        return <WidgetError />;
    }

    if (!query?.data) {
        return <div>null</div>;
    }

    const dataTreated = prepareChartData(
        query?.data,
        describeData?.[entity].attributes.find(({ name }) => name === field),
        userList,
    );

    const config: RadarConfig = {
        data: dataTreated,

        xField: 'name',
        yField: 'value',

        meta: {
            value: {
                min: 0,
                nice: true,
            },
        },

        xAxis: {
            line: null,
            tickLine: null,
        },

        yAxis: {
            label: false,
            grid: {
                alternateColor: 'rgba(0, 0, 0, 0.04)',
            },
        },

        point: {},
        area: {},

        className: 'chart',
    };

    return <Radar {...config} ref={chartRef} />;
};
